import { LaunchStrategy, OpenContext } from '../types';
import { AppChecker, State } from '../webview/AppChecker';
import { checkAppInstalledInQQ } from '../webview/checkers';
import { IS_QQ } from '../util/ua';


export interface QQLaunchStrategyOptions {
  /**
   * - ios中某些情况下可能会检测不到app已安装(请检查app的配置, 此方法仅作为备用)
   * - 使用此选项可以不用QQ的检测方式, 而是和浏览器使用相同的方法
   * - 如果使用此方式，未安装app的情况下则会出现两次弹窗
   */
  skipCheck?: boolean;
  /**
   * 包名，用于android检测App
   */
  packageName: string;
  /**
   * scheme链接, 用于ios检测App
   */
  packageUrl: string;
  /**
   * 下载器相关设置，似乎要在白名单的域名内才能生效
   */
  downloader?: {
    /**
     * 提供给QQ下载器mqq.downloadApp调用
     */
    via: string;
    /**
     * 提供给QQ下载器调用mqq.downloadApp调用
     */
    appid: string;
    /**
     * 获取渠道号的函数，提供给下载器使用，如果不填则默认使用context.model.confid的值
     * @param context - 拉起app时的上下文
     */
    getConfid?: (context: OpenContext) => string | number;
  }
}

/**
 * 创建在QQ webview内的拉起策略
 * @param options - 提供给QQ的jsapi的参数
 * @example
 * ```
 * new AppLauncher({
 *   customLaunch: [
 *     createQQLaunchStrategy({
 *       packageName: 'com.tencent.qqlive',
 *       packageUrl: IS_IOS ? 'tenvideo2://' : 'txvideo://',
 *       appID: 'wxca942bbff22e0e51',
 *       downloader: {
 *         developer: '腾讯公司',
 *         permissionUrl: 'https://dldir1.qq.com/weixin/android/wechat_webview_download_appinfo_new.html?fun=permission&package=com.tencent.qqlive',
 *         privacyAgreementUrl: 'https://m.v.qq.com/about/privacy-policy.html',
 *         getAppVersion,
 *         icon: 'http://i.gtimg.cn/qqlive/images/20150608/logo_app.png',
 *         name: '腾讯视频',
 *         getConfid: context => context.model.confid as string ?? '',
 *         async getMd5(context) {
 *           const res = await axios.get<{ md5: string}>(
 *             `//mcgi.v.qq.com/commdatav2?cmd=39&confid=${context.model.confid ?? ''}`,
 *             { adapter: jsonpAdapter },
 *           );
 *           return res.data.md5;
 *         },
 *       },
 *     },
 *   })]
 * })
 * ```
 */
export function createQQLaunchStrategy(options: QQLaunchStrategyOptions): LaunchStrategy {
  return async function (context, launcher, next) {
    if (IS_QQ) {
      const { logger } = context;
      logger.logKV('Environment', 'mqq');

      let isAppInstalled = false;
      if (!options.skipCheck)  {
        isAppInstalled = await checkAppInstalledInQQ({
          packageName: options.packageName,
          packageUrl: options.packageUrl,
        });
      }

      logger.logKV('App Installed', isAppInstalled);
      let isSuccess = false;
      if (isAppInstalled || options.skipCheck) {
        // 尝试拉起
        isSuccess = await launcher.launchByLocation(context.schemeUrl);
        // 如果没有跳过检查，则可以认为拉起成功
        if (!options.skipCheck) {
          isSuccess = true;
        }
      }
      if (!isSuccess) {
        if (options.downloader) {
          const checker = new AppChecker({
            app_version: '',
            confid: options.downloader?.getConfid?.(context) ?? context.model.confid as string ?? '0',
            developer: '',
            icon: '',
            name: '',
            permission_url: '',
            playerDownloadCallback: '',
            privacy_agreement_url: '',
            md5: () => '',
            // 上面是微信相关部分，不用填写
            package: options.packageName,
            packageProtocol: options.packageUrl,
            via: options.downloader?.appid ?? '',
            appid: options.downloader?.via ?? '',
          });
          await checker.waitUntilReady();
          if (checker.downloader && context.isDownloadLink) {
            const state = await checker.getResult();
            logger.log('use qq downloader');
            if (state === State.DOWNLOADED || state === State.INSTALLING) {
              checker.downloader.install();
            } else {
            // 未安装
              checker.downloader.start({
                confid: options.downloader?.getConfid?.(context) ?? context.model.confid as string ?? '0',
                url: context.fallbackUrl,
              });
            }
          } else {
            launcher.launchByLocation(context.fallbackUrl, { isCheck: false });
          }
        } else {
          launcher.launchByLocation(context.fallbackUrl, { isCheck: false });
        }
      }

      return isSuccess;
    }
    return next();
  };
}
